import { useState } from "react";
import { FONT, COLOR } from "../lib/tokens";

// AppetiteFunnel — the "explore my appetite" drill-down shown when the
// recipe draft detects an OPEN axis in the prompt (e.g. "chicken with
// sauce" → the SAUCE is the thing to discover). Two steps, mood-first:
//
//   Step 1  "What are you craving?"  → 4 flavor moods
//   Step 2  the chosen mood          → 4-5 specific cross-cuisine lanes
//                                       (French Dijon cream, Mexican rojo…)
//
// The whole tree arrives in one Haiku call (funnel prop), so step 2 is
// instant — no per-tap latency. Picking a lane hands { lane, mood } back;
// the parent turns it into a clarification answer that anchors the draft.
//
// Props:
//   funnel     — { axis, moodQuestion, moods:[{id,label,lanes:[{id,label,blurb}]}] }
//   onPick     — (lane, mood) => void  — final lane choice
//   onChefPick — (mood|null) => void   — "surprise me" (within a mood, or wide)
//   onBasic    — () => void            — skip the exploration: cook the
//                prompt straight, zero flavor spin (explicit directive)
//   onReroll   — (keepMoods|null) => void — fresh ideas that never repeat
//                what's been shown. null = 4 new moods (step 1);
//                [labels] = same moods, all-new lanes (step 2)
//   rerolling  — bool, disables/labels the reroll buttons in flight
//   onBack     — () => void            — bail out of the funnel entirely

export default function AppetiteFunnel({
  funnel, onPick, onChefPick, onBasic, onReroll, rerolling = false, onBack,
}) {
  // Drill state keys on the mood LABEL, not id — a drilled-view
  // reroll returns the same moods with fresh lanes but Haiku mints
  // new ids; matching by label keeps the user parked on their mood
  // while the lanes swap underneath.
  const [moodLabel, setMoodLabel] = useState(null);
  const moods = Array.isArray(funnel?.moods) ? funnel.moods : [];
  const activeMood = moodLabel ? moods.find((m) => m.label === moodLabel) : null;
  const axis = (funnel?.axis || "direction").toString();

  const wrap = { padding: "32px 22px 48px", maxWidth: 460, margin: "0 auto" };
  const kicker = {
    fontFamily: FONT.forma, fontSize: 12, fontWeight: 700, letterSpacing: "0.14em",
    color: COLOR.leaf, textTransform: "uppercase", marginBottom: 8,
  };
  const title = {
    fontFamily: FONT.itemTitle, fontStyle: "italic", fontSize: 26,
    lineHeight: 1.12, color: COLOR.ink,
  };
  const backChip = {
    background: "transparent", border: "none", padding: 0, marginBottom: 16,
    fontFamily: FONT.forma, fontSize: 13, fontWeight: 600, letterSpacing: "0.04em",
    color: COLOR.dim, cursor: "pointer", touchAction: "manipulation",
  };
  const chefBtn = {
    marginTop: 20, width: "100%", padding: "13px",
    background: "transparent", border: `1px dashed ${COLOR.edge}`, borderRadius: 12,
    fontFamily: FONT.forma, fontSize: 13, fontWeight: 600, letterSpacing: "0.04em",
    color: COLOR.dim, cursor: "pointer", touchAction: "manipulation",
  };

  // ── Step 2 — lanes for the chosen mood ──────────────────────────────
  if (activeMood) {
    return (
      <div style={wrap}>
        <button onClick={() => setMoodLabel(null)} className="mcm-focusable" style={backChip}>
          ‹ {funnel?.moodQuestion || "Back to cravings"}
        </button>
        <div style={kicker}>{activeMood.label}</div>
        <div style={title}>Pick your {axis}</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 20 }}>
          {activeMood.lanes.map((lane) => (
            <button
              key={lane.id}
              onClick={() => onPick?.(lane, activeMood)}
              className="mcm-focusable"
              style={{
                display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 3,
                textAlign: "left", width: "100%", padding: "14px 16px",
                background: COLOR.glassFillHeavy, border: `1px solid ${COLOR.edge}`,
                borderRadius: 14, cursor: "pointer", touchAction: "manipulation",
              }}
            >
              <span style={{ fontFamily: FONT.itemTitle, fontStyle: "italic", fontSize: 18, color: COLOR.ink }}>
                {lane.label}
              </span>
              {lane.blurb && (
                <span style={{ fontFamily: FONT.forma, fontSize: 12, color: COLOR.dim, letterSpacing: "0.02em" }}>
                  {lane.blurb}
                </span>
              )}
            </button>
          ))}
        </div>
        {onReroll && (
          <button
            onClick={() => onReroll(moods.map((m) => m.label))}
            disabled={rerolling}
            className="mcm-focusable"
            style={{ ...chefBtn, color: COLOR.leaf, borderColor: COLOR.leaf, opacity: rerolling ? 0.6 : 1 }}
          >
            {rerolling ? "Finding fresh ideas…" : "Fresh ideas — same craving, nothing repeated"}
          </button>
        )}
        <button onClick={() => onChefPick?.(activeMood)} className="mcm-focusable" style={chefBtn}>
          Surprise me in this lane
        </button>
      </div>
    );
  }

  // ── Step 1 — craving moods ──────────────────────────────────────────
  return (
    <div style={wrap}>
      {onBack && (
        <button onClick={onBack} className="mcm-focusable" style={backChip}>‹ Back</button>
      )}
      <div style={kicker}>Explore your {axis}</div>
      <div style={title}>{funnel?.moodQuestion || "What are you craving?"}</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 20 }}>
        {moods.map((m) => (
          <button
            key={m.id}
            onClick={() => setMoodLabel(m.label)}
            className="mcm-focusable"
            style={{
              padding: "20px 14px", minHeight: 76,
              background: COLOR.glassFillHeavy, border: `1px solid ${COLOR.edge}`,
              borderRadius: 14, cursor: "pointer", touchAction: "manipulation",
              fontFamily: FONT.itemTitle, fontStyle: "italic", fontSize: 18, color: COLOR.ink,
              display: "flex", alignItems: "center", justifyContent: "center", textAlign: "center",
            }}
          >
            {m.label}
          </button>
        ))}
      </div>
      {/* The bypass leads the action stack — a solid, unmissable
          "skip the exploration" for cooks who want the prompt made
          straight. Owner-specific ask; do not demote below the
          discovery buttons. */}
      {onBasic && (
        <button
          onClick={onBasic}
          className="mcm-focusable"
          style={{
            ...chefBtn,
            border: `1px solid ${COLOR.ink}`,
            color: COLOR.ink,
            fontWeight: 700,
          }}
        >
          Skip — just cook it straight
        </button>
      )}
      {onReroll && (
        <button
          onClick={() => onReroll(null)}
          disabled={rerolling}
          className="mcm-focusable"
          style={{ ...chefBtn, color: COLOR.leaf, borderColor: COLOR.leaf, opacity: rerolling ? 0.6 : 1 }}
        >
          {rerolling ? "Finding fresh ideas…" : "Show me 4 fresh directions"}
        </button>
      )}
      <button onClick={() => onChefPick?.(null)} className="mcm-focusable" style={chefBtn}>
        Just surprise me
      </button>
    </div>
  );
}
